import React, { useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import sort from "fast-sort";
import styled from "styled-components";
import "react-confirm-alert/src/react-confirm-alert.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import { State } from "src/state/ducks";
import * as NotificationActions from "src/state/ducks/notification/actions";
import { formatDateToDate } from "src/helpers/UtilityHelper";

import StyleConfig from "src/constants/StyleConfig";
import { DefaultPageProps, HistoryProps } from "src/constants/Interface";

import { DefaultLayout } from "src/components/layouts/DefaultLayout";
import Heading from "src/components/atoms/typo/Heading";
import PlainHomeButton from "src/components/atoms/button/PlainHomeButton";
import SpinnerModal from "src/components/organisms/loading/SpinnerModal";
import ReservationHistoryList from "src/components/organisms/list/ReservationHistoryList";
import ConfirmModal from "src/components/organisms/modal/ConfirmModal";

type OwnProps = {
  id: string;
  notification_template_ids: string;
};

type Props = DefaultPageProps &
  HistoryProps &
  OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const ReservationHistoryPage = (props: Props) => {
  const {
    login_shop_id,
    getReservations,
    cancelReservation,
    isFetching,
    reservations,
  } = props;

  // 初期表示：予約情報の取得
  useEffect(() => {
    getReservations(login_shop_id);
  }, [getReservations, login_shop_id]);

  // キャンセル完了モーダル
  const onCompleteCancel = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmModal
          title="配信キャンセルが完了しました"
          contentText="配信をキャンセルしました。"
          onClose={onClose}
          closeText="とじる"
          color="green"
        />
      ),
    });
  };

  // キャンセル（下書きに戻す）
  const onCancel = useCallback(
    async (id: string) => {
      confirmAlert({
        customUI: ({ onClose }) => (
          <ConfirmModal
            title="配信キャンセル確認画面"
            contentText="配信をキャンセルしますか？（下書き状態に戻ります）"
            closeText="もどる"
            onClose={onClose}
            submitText="キャンセルする"
            onSubmit={async () => {
              cancelReservation(id);
              onClose();
              onCompleteCancel();
            }}
            color="green"
          />
        ),
      });
    },
    [cancelReservation]
  );

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <div className="p-ReservationHistoryPage">
          <Heading tag="h1" text={`配信履歴を確認する`} />

          <ReservationHistoryList
            notifications={reservations}
            onCancel={onCancel}
          />

          <div className="item">
            <PlainHomeButton />
          </div>
        </div>
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M20};
  .p-ReservationHistoryPage {
    > .item {
      margin: ${StyleConfig.HELPER.M24} auto;
    }
  }
`;

const mapStateToProps = (state: State) => {
  const reservations = state.notification.reservations.resource;
  const sorted = sort(reservations)
    .by([
      {
        desc: (v) => {
          const { scheduled_time } = v;
          return scheduled_time
            ? formatDateToDate(scheduled_time).getTime()
            : false;
        },
      },
    ])
    .filter((v) => !v.is_draft);
  return {
    reservations: sorted,
    isFetching: state.notification.reservations.isFetching,
    error: state.notification.reservations.apiError,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getReservations: (shopId: string) =>
    dispatch(NotificationActions.getReservations({ shopId })),
  cancelReservation: (id: string) =>
    dispatch(NotificationActions.cancelReservation({ id })),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReservationHistoryPage);
